import { FormattedMessage } from 'react-intl';
import { Outlet, useLocation, useSearchParams } from 'react-router';

import { ErrorBoundary } from '@sorare/error-boundary';
import { Suspense } from '@sorare/core/src/atoms/Suspense';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconLightningBolt } from '@sorare/core/src/atoms/icons/system/iconLightningBolt';
import { ContentWithStickyHeader } from '@sorare/core/src/atoms/layout/ContentWithStickyHeader';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { SearchLayoutRoot } from '@sorare/core/src/components/search/SearchLayout/ui';
import {
  ANY_SPORT_MARKET_SHOP_AUCTIONS,
  ANY_SPORT_MARKET_SHOP_INSTANT_BUY,
  ANY_SPORT_MARKET_SHOP_MANAGER_SALES,
  ANY_SPORT_MARKET_SHOP_MANAGER_SALES_PLAYERSLUG_RARITY,
  ANY_SPORT_MARKET_SHOP_MATCHDAY_PICKS,
  ANY_SPORT_SERIES_MARKET_COMMON_PACKS_GROUP,
} from '@sorare/core/src/constants/__generated__/routes';
import { NoIndex } from '@sorare/core/src/contexts/seo';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useMatchdayPicksTab } from '@sorare/core/src/hooks/market/useMatchdayPicksTab';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { navLabels, transferMarket } from '@sorare/core/src/lib/glossary';
import { matchAnySportPath } from '@sorare/core/src/lib/routing/generateSportPath';
import { MarketBanners } from '@sorare/marketplace/src/components/MarketBanners';
import { DisabledMarket } from '@sorare/marketplace/src/components/market/DisabledMarket';
import { MarketShopHeader } from '@sorare/marketplace/src/components/market/MarketShopHeader';
import { EnterFromAndroidMarketPlaceTab } from '@sorare/marketplace/src/components/mobileApp/EnterFromAndroidMarketplaceTab';
import { CurrentMarketplaceUserProvider } from '@sorare/marketplace/src/contexts/currentMarketplaceUser/Provider';
import {
  buildMarketTabRoute,
  useNavigableMarketTabParams,
} from '@sorare/marketplace/src/hooks/useNavigableMarketTabParams';
import { FROM_NEW_SEASON_PARAM } from '@sorare/marketplace/src/lib/newSeason';

const MarketShopLayout = () => {
  const { sport, generateSportContextPath } = useSportContext();

  const { isIosApp, canUseIosPostMessageAndForYou, isAndroidApp } =
    useIsMobileApp();

  const {
    flags: { buyOnWebMarketplaceFromAndroid = false },
  } = useFeatureFlags();

  const { filterQuery, filterQueryWithInSeason } =
    useNavigableMarketTabParams();

  const { show: showMatchdayPicks } = useMatchdayPicksTab({ sport });

  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();

  const fromNewSeason = searchParams.has(FROM_NEW_SEASON_PARAM);

  const getMarketTabRoute = (route: string, inSeason?: boolean) => {
    const query = inSeason ? filterQueryWithInSeason : filterQuery;
    const fullQuery = fromNewSeason
      ? [query, `${FROM_NEW_SEASON_PARAM}=true`].filter(Boolean).join('&')
      : query;
    return buildMarketTabRoute(route, fullQuery);
  };

  const disabledMarket = isAndroidApp && buyOnWebMarketplaceFromAndroid;
  const content = (
    <Suspense fallback={<LoadingIndicator fullHeight />}>
      <CurrentMarketplaceUserProvider sport={sport}>
        {disabledMarket ? (
          <FullWidthContainer.NoPadding>
            <DisabledMarket />
          </FullWidthContainer.NoPadding>
        ) : (
          <Outlet />
        )}
      </CurrentMarketplaceUserProvider>
    </Suspense>
  );

  if (
    matchAnySportPath(
      ANY_SPORT_SERIES_MARKET_COMMON_PACKS_GROUP,
      pathname,
      sport
    )
  ) {
    return content;
  }

  return (
    <div>
      <NoIndex />
      <EnterFromAndroidMarketPlaceTab />
      <MarketShopHeader />
      <ErrorBoundary fallback={null}>
        <MarketBanners />
      </ErrorBoundary>

      <ContentWithStickyHeader
        hideHeader={!!isIosApp && !canUseIosPostMessageAndForYou}
        header={
          <TabBar fullWidth variant="flat" ignoreQueryParams>
            {!!showMatchdayPicks && (
              <Tab
                to={getMarketTabRoute(
                  generateSportContextPath(ANY_SPORT_MARKET_SHOP_MATCHDAY_PICKS)
                )}
                matches={[ANY_SPORT_MARKET_SHOP_MATCHDAY_PICKS]}
              >
                <Horizontal gap={0.5} center>
                  <FontAwesomeIcon icon={iconLightningBolt} size="sm" />
                  <FormattedMessage {...transferMarket.matchdayPicks} />
                </Horizontal>
              </Tab>
            )}
            <Tab
              to={getMarketTabRoute(
                generateSportContextPath(ANY_SPORT_MARKET_SHOP_AUCTIONS)
              )}
            >
              <FormattedMessage {...navLabels.auctions} />
            </Tab>
            <Tab
              to={getMarketTabRoute(
                generateSportContextPath(ANY_SPORT_MARKET_SHOP_INSTANT_BUY)
              )}
              matches={[ANY_SPORT_MARKET_SHOP_INSTANT_BUY]}
            >
              <FormattedMessage {...transferMarket.instantBuy} />
            </Tab>
            <Tab
              to={getMarketTabRoute(
                generateSportContextPath(ANY_SPORT_MARKET_SHOP_MANAGER_SALES),
                true
              )}
              matches={[
                ANY_SPORT_MARKET_SHOP_MANAGER_SALES,
                ANY_SPORT_MARKET_SHOP_MANAGER_SALES_PLAYERSLUG_RARITY,
              ]}
            >
              <FormattedMessage {...navLabels.purchases} />
            </Tab>
          </TabBar>
        }
      >
        <SearchLayoutRoot>{content}</SearchLayoutRoot>
      </ContentWithStickyHeader>
    </div>
  );
};

export default MarketShopLayout;
